import { Stock } from '@inventory/core/domain/entities';
import type { IStockRepository } from '@inventory/core/domain/interfaces';
import { BasePouchDBAdapter } from './BasePouchDBAdapter';
import type { BasePouchDocument } from '../types';

interface StockPouchDocument extends BasePouchDocument {
  productId: string;
  warehouseId: string;
  locationId: string;
  quantity: number;
  lastMovementDate: string;
  lastCountDate?: string;
  status: 'available' | 'reserved' | 'damaged' | 'in_transit' | 'expired';
  variantId?: string;
  lotNumber?: string;
  serialNumber?: string;
  expirationDate?: string;
  palletSpaces?: number;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  metadata?: {
    restrictions?: string[];
    storageConditions?: string[];
    notes?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export class StockPouchDBAdapter extends BasePouchDBAdapter<Stock> implements IStockRepository {
  private indicesReady: Promise<void>;

  constructor(dbName: string) {
    super(dbName, 'stock');
    this.indicesReady = this.createIndices().catch(() => {});
  }

  async save(entity: Stock): Promise<void> {
    console.log('Stock pouch DB Adapter Saving stock entity:', entity);
    const doc = this.toDocument(entity);
    try {
      const response = await this.db.put(doc);
      console.log('PouchDB put response:', response);
    } catch (error) {
      console.error('Error saving to PouchDB:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const doc = await this.db.get(id);
      await this.db.remove(doc);
    } catch (error) {
      if ((error as any).name === 'not_found') {
        return;
      }
      throw error;
    }
  }

  private async createIndices(): Promise<void> {
    await this.createIndex(['productId']);
    await this.createIndex(['warehouseId']);
    await this.createIndex(['locationId']);
    await this.createIndex(['status']);
  }

  protected toDocument(data: Stock): StockPouchDocument {
    return {
      _id: data._id,
      id: data._id,
      type: data.type,
      version: data.version,
      productId: data.productId,
      warehouseId: data.warehouseId,
      locationId: data.locationId,
      quantity: data.quantity,
      status: data.status,
      lastMovementDate: data.lastMovementDate.toISOString(),
      lastCountDate: data.lastCountDate?.toISOString(),
      variantId: data.variantId,
      lotNumber: data.lotNumber,
      serialNumber: data.serialNumber,
      expirationDate: data.expirationDate?.toISOString(),
      palletSpaces: data.palletSpaces ?? 0,
      weight: data.weight,
      dimensions: data.dimensions,
      metadata: data.metadata,
      createdAt: data.createdAt.toISOString(),
      updatedAt: data.updatedAt.toISOString(),
    };
  }

  protected fromDocument(doc: StockPouchDocument): Stock {
    return Stock.restore({
      _id: doc._id ?? doc.id,
      type: doc.type,
      version: doc.version ?? 1,
      productId: doc.productId,
      warehouseId: doc.warehouseId,
      locationId: doc.locationId,
      quantity: doc.quantity,
      status: doc.status,
      lastMovementDate: new Date(doc.lastMovementDate),
      lastCountDate: doc.lastCountDate ? new Date(doc.lastCountDate) : undefined,
      variantId: doc.variantId,
      lotNumber: doc.lotNumber,
      serialNumber: doc.serialNumber,
      expirationDate: doc.expirationDate ? new Date(doc.expirationDate) : undefined,
      palletSpaces: doc.palletSpaces,
      weight: doc.weight,
      dimensions: doc.dimensions,
      metadata: doc.metadata,
      createdAt: new Date(doc.createdAt),
      updatedAt: new Date(doc.updatedAt),
    });
  }

  async findByProduct(productId: string): Promise<Stock[]> {
    console.log('StockPouchDBAdapter.findByProduct called with:', productId);
    const result = await this.db.allDocs({ include_docs: true });
    return result.rows
      .map(row => row.doc)
      .filter(doc => doc && (doc as any).type === 'stock' && (doc as any).productId === productId)
      .map(doc => this.fromDocument(doc as unknown as StockPouchDocument));
  }

  async findByWarehouse(warehouseId: string): Promise<Stock[]> {
    console.log('StockPouchDBAdapter.findByWarehouse called with:', warehouseId);
    try {
      const result = await this.db.find({
        selector: {
          type: 'stock',
          warehouseId: warehouseId
        }
      });
      return result.docs.map(doc => this.fromDocument(doc as StockPouchDocument));
    } catch (error) {
      console.error('Error querying stocks by warehouse ID from PouchDB:', error);
      throw error;
    }
  }

  async findByLocation(locationId: string): Promise<Stock[]> {
    console.log('StockPouchDBAdapter.findByLocation called with:', locationId);
    try {
      const result = await this.db.find({
        selector: {
          type: 'stock',
          locationId: locationId
        }
      });
      return result.docs.map(doc => this.fromDocument(doc as StockPouchDocument));
    } catch (error) {
      console.error('Error querying stocks by location ID from PouchDB:', error);
      throw error;
    }
  }

  async findAvailable(warehouseId: string): Promise<Stock[]> {
    console.log('StockPouchDBAdapter.findAvailable called');
    try {
      const result = await this.db.find({
        selector: {
          type: 'stock',
          warehouseId: warehouseId,
          status: 'available'
        }
      });
      return result.docs.map(doc => this.fromDocument(doc as StockPouchDocument));
    } catch (error) {
      console.error('Error querying available stocks from PouchDB:', error);
      throw error;
    }
  }


}